import React, { useEffect, useState, useCallback } from 'react';
import { MessageCircle, FileText, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';

const WHATSAPP_NUMBER = "5565992258000";
const WHATSAPP_MESSAGE = "Olá! Gostaria de solicitar um orçamento para climatização.";

const slides = [
  {
    image: "https://customer-assets.emergentagent.com/job_tokefrio-launch/artifacts/abub61c4_image.png",
    title: "Soluções completas em climatização para empresas",
    subtitle: "Projetos, execução de obras e manutenção com foco em eficiência e confiabilidade.",
  },
  {
    image: "https://customer-assets.emergentagent.com/job_tokefrio-launch/artifacts/dp3f795m_image.png",
    title: "Instalação e manutenção profissional",
    subtitle: "Equipe técnica qualificada para atender todas as necessidades da sua empresa.",
  },
  {
    image: "https://customer-assets.emergentagent.com/job_tokefrio-launch/artifacts/go6kz0nt_image.png",
    title: "Manutenção preventiva e corretiva",
    subtitle: "Garantimos o funcionamento eficiente dos seus sistemas de climatização.",
  },
];

export const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  }, []);

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 5000);
  };

  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(nextSlide, 5000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, nextSlide]);

  const handleWhatsApp = () => {
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`, '_blank');
  };

  const scrollToContact = () => {
    document.getElementById('orcamento')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center pt-20 overflow-hidden"
    >
      {/* Carousel Background */}
      <div className="absolute inset-0 z-0">
        {slides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
            data-testid={`carousel-slide-${index}`}
          >
            <img
              src={slide.image}
              alt={slide.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-sky-900/80 via-sky-800/60 to-sky-700/30"></div>
          </div>
        ))}
      </div>

      {/* Carousel Navigation Arrows */}
      <button
        onClick={prevSlide}
        className="absolute left-4 lg:left-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all duration-300 hover:scale-110"
        data-testid="carousel-prev-button"
        aria-label="Slide anterior"
      >
        <ChevronLeft className="w-6 h-6 text-slate-700" />
      </button>

      <button
        onClick={nextSlide}
        className="absolute right-4 lg:right-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all duration-300 hover:scale-110"
        data-testid="carousel-next-button"
        aria-label="Próximo slide"
      >
        <ChevronRight className="w-6 h-6 text-slate-700" />
      </button>

      {/* Carousel Dots */}
      <div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex items-center gap-3"
        data-testid="carousel-dots"
      >
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`transition-all duration-300 rounded-full ${
              index === currentSlide
                ? 'w-10 h-3 bg-sky-600'
                : 'w-3 h-3 bg-slate-300 hover:bg-slate-400'
            }`}
            data-testid={`carousel-dot-${index}`}
            aria-label={`Ir para slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-32">
        <div className="max-w-2xl text-left">
          {/* Overline */}
          <div
            className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full mb-6 animate-fade-in"
            data-testid="hero-overline"
          >
            <span className="font-manrope text-sm font-bold tracking-wide uppercase">
              Climatização Profissional
            </span>
          </div>

          {/* Dynamic Headline */}
          <h1
            className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.1] mb-6 animate-fade-in-up"
            data-testid="hero-headline"
            key={currentSlide}
          >
            {slides[currentSlide].title}
          </h1>

          {/* Dynamic Subheadline */}
          <p
            className="font-manrope text-lg sm:text-xl text-white/90 leading-relaxed mb-8 animate-fade-in-up animation-delay-100"
            data-testid="hero-subheadline"
            key={`sub-${currentSlide}`}
          >
            {slides[currentSlide].subtitle}
            {' '}Atendemos empresas em Cuiabá e região com mais de 10 anos de experiência no mercado.
          </p>

          {/* CTA Buttons */}
          <div
            className="flex flex-col sm:flex-row gap-4 animate-fade-in-up animation-delay-200"
            data-testid="hero-cta-container"
          >
            <Button
              onClick={scrollToContact}
              className="bg-sky-600 hover:bg-sky-700 text-white font-semibold rounded-full px-8 py-4 text-base transition-all btn-primary shadow-lg shadow-sky-600/20"
              data-testid="hero-orcamento-button"
            >
              <FileText className="w-5 h-5 mr-2" />
              Solicitar Orçamento
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>

            <Button
              onClick={handleWhatsApp}
              variant="outline"
              className="bg-green-500 hover:bg-green-600 text-white font-semibold rounded-full px-8 py-4 text-base transition-all border-0 whatsapp-pulse"
              data-testid="hero-whatsapp-button"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              Falar no WhatsApp
            </Button>
          </div>

          {/* Trust indicators */}
          <div
            className="flex flex-wrap items-center gap-6 mt-12 pt-8 border-t border-white/20 animate-fade-in-up animation-delay-300"
            data-testid="hero-trust-indicators"
          >
            <div className="flex items-center gap-2">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                <span className="font-outfit text-xl font-bold text-white">10+</span>
              </div>
              <span className="font-manrope text-sm text-white/90">Anos de<br />experiência</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                <span className="font-outfit text-lg font-bold text-white">MT</span>
              </div>
              <span className="font-manrope text-sm text-white/90">Cuiabá e<br />região</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                <span className="font-outfit text-lg font-bold text-white">B2B</span>
              </div>
              <span className="font-manrope text-sm text-white/90">Foco<br />empresarial</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
